<template>
  <div
    class="card res-card"
    :class="doctor.is_online ? 'res-card--online' : ''"
  >
    <!-- Avatar -->
    <div class="av">
      <div
        class="avatar avatar--lg"
        :class="doctor.is_online ? 'avatar--ring' : ''"
      >
        <img v-if="doctor.photo" :src="doctor.photo" :alt="doctor.name" />
        <span v-else>{{ initials }}</span>
      </div>
      <span v-if="doctor.is_online" class="on-dot" />
    </div>

    <!-- Informações -->
    <div class="res-info">
      <span class="nm">{{ doctor.name }}</span>
      <span class="meta">
        {{ doctor.doctor_property.medical_specialty_name }}
        · {{ doctor.doctor_property.council_description }}
      </span>
      <span v-if="doctorRating" class="rate">
        <span class="st">★</span>
        {{ doctorRating }}
        <small v-if="doctorReviewCount > 0">({{ ratingCountLabel }})</small>
      </span>
      <div class="tags">
        <span
          class="badge"
          :class="doctor.is_online ? 'badge--success' : 'badge--info'"
        >
          {{ availabilityLabel }}
        </span>
        <span class="badge badge--neutral badge--no-dot">Por vídeo</span>
      </div>
    </div>

    <!-- Preço + Ações -->
    <div class="res-side">
      <div class="price">
        {{ doctorPrice }}
        <small>por consulta</small>
      </div>
      <div class="acts">
        <button
          v-if="express"
          class="btn btn--success btn--block"
          @click="goToExpressAppointment"
        >
          Consultar agora
        </button>
        <button
          v-else
          class="btn btn--accent btn--block"
          @click="goToDoctorDetails(doctor.id)"
        >
          Agendar consulta
        </button>
        <button
          v-if="!express"
          class="btn btn--outline btn--block"
          @click="goToDoctorDetails(doctor.id)"
        >
          Ver perfil
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { intToBRL } from "../../utils/currency/convert";
import { getNameInitials } from "../../utils/getNameInitials";

export default {
  name: "DoctorsListCard",
  props: {
    doctor: {
      type: Object,
      default: () => {}
    },
    express: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    ...mapActions("appointments", ["holdScheduleAppointment"]),
    // Paciente logado e não-confirmado não pode iniciar nenhum fluxo de
    // agendamento (nem ver perfil, nem consulta expressa) — mostra o modal
    // de confirmação de e-mail em vez de navegar.
    blockedByEmailConfirmation() {
      if (this.loggedIn && !this.isEmailConfirmed) {
        this.$bvModal.show("email-confirmation-required");
        return true;
      }
      return false;
    },
    goToDoctorDetails(doctorId) {
      if (this.blockedByEmailConfirmation()) return;
      this.$router.push({
        name: "DoctorDetails",
        params: { doctor: doctorId }
      });
    },
    goToExpressAppointment() {
      if (this.blockedByEmailConfirmation()) return;
      const scheduledAt = new Date(Date.now() + 30 * 60 * 1000);
      this.holdScheduleAppointment({
        doctor_id: this.doctor.id,
        doctor: this.doctor,
        scheduled_at: scheduledAt.toISOString()
      });
      this.$router.push({ name: "Schedule" });
    }
  },
  computed: {
    ...mapGetters("auth", ["loggedIn"]),
    ...mapGetters("user", ["isEmailConfirmed"]),
    initials() {
      return this.doctor.name ? getNameInitials(this.doctor.name) : "?";
    },
    doctorPrice() {
      return intToBRL(
        this.doctor.doctor_property.price_cents_and_earning_margin_sum
      );
    },
    doctorRating() {
      const media = this.doctor.rating_average;
      if (media === null || media === undefined) return null;
      return Number(media)
        .toFixed(1)
        .replace(".", ",");
    },
    doctorReviewCount() {
      return this.doctor.rating_count || 0;
    },
    ratingCountLabel() {
      const n = this.doctorReviewCount;
      return `${n} ${n === 1 ? "avaliação" : "avaliações"}`;
    },
    availabilityLabel() {
      if (this.doctor.is_online) {
        return "Disponível agora";
      }
      if (this.doctor.next_available && this.doctor.next_available_time) {
        const date = new Date(this.doctor.next_available);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const isToday = date.toDateString() === today.toDateString();
        const isTomorrow = date.toDateString() === tomorrow.toDateString();

        const time = this.doctor.next_available_time.substring(0, 5);

        if (isToday) {
          return `Disponível hoje · ${time}`;
        } else if (isTomorrow) {
          return `Próximo: amanhã ${time}`;
        } else {
          const dayName = date
            .toLocaleDateString("pt-BR", { weekday: "short" })
            .replace(".", "");
          return `Próximo: ${dayName} ${time}`;
        }
      }
      return "Próximo: em breve";
    }
  }
};
</script>
